import { useCallback, useEffect, useRef } from "react";

// Recovery only reads the existing snapshot. It never starts a collector.
export default function useRecoveringSnapshot({ scopeKey, read, accept, fail, loading, reset, needsRetry, scopeIsCurrent }) {
  const callbacks = useRef({ read, accept, fail, loading, reset, needsRetry, scopeIsCurrent });
  callbacks.current = { read, accept, fail, loading, reset, needsRetry, scopeIsCurrent };
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const state = useRef(null);
  const load = useCallback((options = {}) => {
    const s = state.current;
    if (!s || s.scope !== scopeKey || s.closed || s.stopped || !callbacks.current.scopeIsCurrent()) return Promise.resolve();
    if (s.pending) return s.pending;
    if (Date.now() < s.nextAt && options.manual !== true) return Promise.resolve();
    if (s.timer) { clearTimeout(s.timer); s.timer = null; }
    const valid = () => state.current === s && !s.closed && currentScope.current === s.scope && callbacks.current.scopeIsCurrent();
    const retry = () => {
      if (!valid() || s.stopped) return;
      const delay = Math.min(5000 * 2 ** Math.min(s.attempt++, 3), 30000);
      s.nextAt = Date.now() + delay;
      s.timer = setTimeout(() => { s.timer = null; load(); }, delay);
    };
    callbacks.current.loading(true);
    const controller = new AbortController();
    s.controller = controller;
    s.pending = Promise.resolve().then(() => callbacks.current.read(controller.signal)).then(value => {
      if (!valid()) return;
      callbacks.current.accept(value);
      if (callbacks.current.needsRetry(value)) retry();
      else { s.attempt = 0; s.nextAt = 0; }
    }).catch(error => {
      if (!valid() || controller.signal.aborted) return;
      const denied = [401, 403].includes(error?.status);
      s.stopped = denied;
      callbacks.current.fail(error, denied);
      if (!denied) retry();
    }).finally(() => {
      s.pending = null;
      if (state.current === s && !s.closed && currentScope.current === s.scope) callbacks.current.loading(false);
    });
    return s.pending;
  }, [scopeKey]);

  useEffect(() => {
    const s = { scope: scopeKey, pending: null, timer: null, attempt: 0, nextAt: 0, stopped: false, closed: false };
    state.current = s;
    callbacks.current.reset();
    return () => { s.closed = true; clearTimeout(s.timer); s.controller?.abort(); };
  }, [scopeKey]);
  return load;
}
